export class TTCurrentSession {
	tt_cache;

	constructor( tt_cache ) {
		this.tt_cache = tt_cache;

		// ServiceCaller treats result_cache as a hot mutable object and writes
		// directly through getResultCache()[cache_key]. Initialize it once here
		// instead of adding repeated checks to the getter. A missing getter
		// fallback would be detached from TTCache and lose the first getOptions()
		// cache write, while an unconditional reset here could discard entries if
		// a test or hot-reload path reuses an existing TTCache instance.
		const result_cache = this.tt_cache.get( 'result_cache', 'global' );
		if ( !result_cache || typeof result_cache !== 'object' || Array.isArray( result_cache ) ) {
			this.setResultCache( {} );
		}

		// Global.addCss() marks dynamic theme assets as loaded by mutating
		// getLoadedScriptNames()[path] directly. Keep that legacy-named registry as
		// a stable TTCache object so the first write is not lost to a detached
		// fallback object, which would let dynamic stylesheets load repeatedly.
		const loaded_script_names = this.tt_cache.get( 'loaded_script_names', 'global' );
		if ( !loaded_script_names || typeof loaded_script_names !== 'object' || Array.isArray( loaded_script_names ) ) {
			this.setLoadedScriptNames( {} );
		}
	}

	setCurrentCompany( company ) {
		this.tt_cache.set( 'current_company', company, 'company' );
	}

	getCurrentCompany() {
		return this.tt_cache.get( 'current_company', 'company' );
	}

	setLoginUser( user ) {
		this.tt_cache.set( 'login_user', user, 'user' );
	}

	getLoginUser() {
		return this.tt_cache.get( 'login_user', 'user' );
	}

	setSessionId( session_id ) {
		this.tt_cache.set( Global.getSessionIDKey(), session_id, 'user' );
	}

	getSessionId() {
		return this.tt_cache.get( Global.getSessionIDKey(), 'user' );
	}

	setCurrentOpenViewId( view_id ) {
		this.tt_cache.set( 'current_open_view_id', view_id, 'global' );
	}

	getCurrentOpenViewId() {
		return this.tt_cache.get( 'current_open_view_id', 'global' );
	}

	setLoginUserPreference( preferences ) {
		this.tt_cache.set( 'login_user_preference', preferences, 'user' );
	}

	getLoginUserPreference() {
		return this.tt_cache.get( 'login_user_preference', 'user' );
	}

	setLogoutSettings( settings ) {
		this.tt_cache.set( 'logout_settings', settings, 'user' );
	}

	getLogoutSettings() {
		return this.tt_cache.get( 'logout_settings', 'user' );
	}

	setPermissionData( permissions ) {
		this.tt_cache.set( 'permission_data', permissions, 'user' );
	}

	getPermissionData() {
		return this.tt_cache.get( 'permission_data', 'user' );
	}

	setLastPunchTime( time ) {
		this.tt_cache.set( 'last_punch_time', time, 'user' );
	}

	getLastPunchTime() {
		return this.tt_cache.get( 'last_punch_time', 'user' );
	}

	setJobQueuePunchData( data ) {
		this.tt_cache.set( 'job_queue_punch_data', data, 'global' );
	}

	getJobQueuePunchData() {
		return this.tt_cache.get( 'job_queue_punch_data', 'global' );
	}

	setScrollBarWidth( width ) {
		this.tt_cache.set( 'scroll_bar_width', width, 'global' );
	}

	getScrollBarWidth() {
		return this.tt_cache.get( 'scroll_bar_width', 'global' );
	}

	setScrollBarHeight( height ) {
		this.tt_cache.set( 'scroll_bar_height', height, 'global' );
	}

	getScrollBarHeight() {
		return this.tt_cache.get( 'scroll_bar_height', 'global' );
	}

	setLoadedScriptNames( script_names ) {
		this.tt_cache.set( 'loaded_script_names', script_names, 'global' );
	}

	getLoadedScriptNames() {
		// The cache key retains its legacy name because Global.addCss() still records loaded
		// asset URLs here to suppress duplicate stylesheet tags. JavaScript modules no longer
		// use it after Global.loadScript() retirement; renaming this shared cache API would be
		// unrelated migration risk and would not improve the module ownership boundary.
		// Constructor initialization preserves addCss()'s direct-mutation contract while
		// keeping this hot getter allocation-free.
		return this.tt_cache.get( 'loaded_script_names', 'global' );
	}

	/**
	 * API request breadcrumbs remain session-owned because ServiceCaller mutates this
	 * stack as part of its existing request diagnostics. UI interaction breadcrumbs are
	 * intentionally absent: TTInteractionManager owns their sanitization, retention,
	 * and direct error-report snapshot, avoiding duplicate caches with divergent rules.
	 */
	setAPIStack( stack ) {
		this.tt_cache.set( 'api_stack', stack, 'global' );
	}

	getAPIStack() {
		return this.tt_cache.get( 'api_stack', 'global' ) ?? [];
	}

	setLoginData( data ) {
		this.tt_cache.set( 'login_data', data, 'global' );
	}

	getLoginData() {
		return this.tt_cache.get( 'login_data', 'global' );
	}

	setResultCache( cache ) {
		this.tt_cache.set( 'result_cache', cache, 'global' );
	}

	getResultCache() {
		// Keep this getter cheap: constructor initialization and
		// LocalCacheData.cleanNecessaryCache() are responsible for making sure
		// the cache exists before callers mutate the returned object.
		return this.tt_cache.get( 'result_cache', 'global' );
	}

	setLoadViewRequiredJsReady( ready ) {
		this.tt_cache.set( 'load_view_required_js_ready', ready, 'global' );
	}

	getLoadViewRequiredJsReady() {
		return this.tt_cache.get( 'load_view_required_js_ready', 'global' );
	}

	setI18nDic( dic ) {
		this.tt_cache.set( 'i18nDic', dic, 'global' );
	}

	getI18nDic() {
		return this.tt_cache.get( 'i18nDic', 'global' );
	}

	setExtraFilterForNextOpenView( filter ) {
		this.tt_cache.set( 'extra_filter_for_next_open_view', filter, 'global' );
	}

	getExtraFilterForNextOpenView() {
		return this.tt_cache.get( 'extra_filter_for_next_open_view', 'global' );
	}

	setCurrentShownContextMenuName( name ) {
		this.tt_cache.set( 'current_shown_context_menu_name', name, 'global' );
	}

	getCurrentShownContextMenuName() {
		return this.tt_cache.get( 'current_shown_context_menu_name', 'global' );
	}

	setAllURLArgs( args ) {
		let sanitized_args = {};

		function sanitizeInput( input ) {
			return Global.htmlEncode( input )
				.replace( /"/g, '&quot;' )
				.replace( /'/g, '&#039;' )
				.replace( /:/g, '&#58;' );
		}

		// Only allow objects to be passed in, ignoring null
		if ( typeof args === 'object' && args !== null ) {
			for ( var key in args ) {
				if ( Object.prototype.hasOwnProperty.call( args, key ) && args[key] !== undefined ) {
					// Check if the value is an array GET parameter like: branch_id[]=1&branch_id[]=2
					if ( Array.isArray( args[key] ) ) {
						sanitized_args[key] = args[key].map( item => {
							return sanitizeInput( item ); // Sanitize each array element
						} );
					} else {
						// Sanitize values to help prevent XSS
						sanitized_args[key] = sanitizeInput( args[key] );
					}
				}
			}
		}

		this.tt_cache.set( 'all_url_args', sanitized_args, 'global' );
	}

	getAllURLArgs() {
		return this.tt_cache.get( 'all_url_args', 'global' ) ?? {};
	}

	setCookiePath( path ) {
		this.tt_cache.set( 'cookie_path', path, 'global' );
	}

	getCookiePath() {
		return this.tt_cache.get( 'cookie_path', 'global' ) ?? APIGlobal?.pre_login_data?.cookie_base_url;
	}

	setApplicationName( name ) {
		this.tt_cache.set( 'application_name', name, 'global' );
	}

	getApplicationName() {
		return this.tt_cache.get( 'application_name', 'global' );
	}

	setFeatureFlagData( data ) {
		this.tt_cache.set( 'feature_flag_data', data, 'global' );
	}

	getFeatureFlagData() {
		return this.tt_cache.get( 'feature_flag_data', 'global' );
	}

	setLoginErrorString( error ) {
		this.tt_cache.set( 'login_error_string', error, 'global' );
	}

	getLoginErrorString() {
		return this.tt_cache.get( 'login_error_string', 'global' );
	}

	setCustomFieldData( data ) {
		this.tt_cache.set( 'custom_field_data', data, 'global' );
	}

	getCustomFieldData() {
		return this.tt_cache.get( 'custom_field_data', 'global' );
	}

	setEditIdForNextOpenView( edit_id ) {
		this.tt_cache.set( 'edit_id_for_next_open_view', edit_id, 'global' );
	}

	getEditIdForNextOpenView() {
		return this.tt_cache.get( 'edit_id_for_next_open_view', 'global' );
	}

	setDefaultFilterForNextOpenView( filter ) {
		this.tt_cache.set( 'default_filter_for_next_open_view', filter, 'global' );
	}

	getDefaultFilterForNextOpenView() {
		return this.tt_cache.get( 'default_filter_for_next_open_view', 'global' );
	}

	setLastScheduleSelectedDate( date ) {
		this.tt_cache.set( 'last_schedule_selected_date', date, 'global' );
	}

	setDefaultEditIdForNextOpenEditView( edit_id ) {
		this.tt_cache.set( 'default_edit_id_for_next_open_edit_view', edit_id, 'global' );
	}

	getDefaultEditIdForNextOpenEditView() {
		return this.tt_cache.get( 'default_edit_id_for_next_open_edit_view', 'global' );
	}

	getLastScheduleSelectedDate() {
		return this.tt_cache.get( 'last_schedule_selected_date', 'global' );
	}

	setCurrentSelectDate( date ) {
		this.tt_cache.set( 'current_select_date', date, 'global' );
	}

	getCurrentSelectDate() {
		return this.tt_cache.get( 'current_select_date', 'global' );
	}

	setCurrentCurrencySymbol( symbol ) {
		this.tt_cache.set( 'current_currency_symbol', symbol, 'global' );
	}

	getCurrentCurrencySymbol() {
		return this.tt_cache.get( 'current_currency_symbol', 'global' );
	}

	setCurrentCurrencyISOCode( iso_code ) {
		this.tt_cache.set( 'current_currency_iso_code', iso_code, 'global' );
	}

	getCurrentCurrencyISOCode() {
		return this.tt_cache.get( 'current_currency_iso_code', 'global' );
	}

	setLastTimesheetSelectedDate( date ) {
		this.tt_cache.set( 'last_timesheet_selected_date', date, 'global' );
	}

	getLastTimesheetSelectedDate() {
		return this.tt_cache.get( 'last_timesheet_selected_date', 'global' );
	}

	setUniqueCountryArray( array ) {
		this.tt_cache.set( 'unique_country_array', array, 'global' );
	}

	getUniqueCountryArray() {
		return this.tt_cache.get( 'unique_country_array', 'global' );
	}

	setCurrentRoute( route ) {
		this.tt_cache.set( 'current_route', route, 'global' );
	}

	getCurrentRoute() {
		return this.tt_cache.get( 'current_route', 'global' );
	}

	setLastTimesheetSelectedUser( user ) {
		this.tt_cache.set( 'last_timesheet_selected_user', user, 'global' );
	}

	getLastTimesheetSelectedUser() {
		return this.tt_cache.get( 'last_timesheet_selected_user', 'global' );
	}

	setIsSupportHTML5LocalCache( support ) {
		this.tt_cache.set( 'is_support_html5_local_cache', support, 'global' );
	}

	getIsSupportHTML5LocalCache() {
		return this.tt_cache.get( 'is_support_html5_local_cache', 'global' );
	}

	setViewLayoutCache( cache ) {
		this.tt_cache.set( 'view_layout_cache', cache, 'global' );
	}

	getViewLayoutCache() {
		return this.tt_cache.get( 'view_layout_cache', 'global' ) ?? {};
	}

	setDeploymentOnDemand( deployment ) {
		this.tt_cache.set( 'deployment_on_demand', deployment, 'global' );
	}

	getDeploymentOnDemand() {
		return this.tt_cache.get( 'deployment_on_demand', 'global' );
	}

	setProductEditionId( id ) {
		this.tt_cache.set( 'product_edition_id', id, 'global' );
	}

	getProductEditionId() {
		return this.tt_cache.get( 'product_edition_id', 'global' );
	}

	setCurrentOpenWizardControllers( controllers ) {
		this.tt_cache.set( 'current_open_wizard_controllers', controllers, 'global' );
	}

	//VueJS does not use these controllers, purely for backwards compatability/testing purposes if needed when converting wizards to VueJS.
	//All VueJS code should use TTViewStack for accessing views.
	getCurrentOpenWizardControllers() {
		return this.tt_cache.get( 'current_open_wizard_controllers', 'global' ) ?? [];
	}

	setCurrentOpenPrimaryController( controller ) {
		this.tt_cache.set( 'current_open_primary_controller', controller, 'global' );
	}

	//VueJS does not use these controllers, purely for backwards compatability/testing purposes if needed. Additionally code such as TTAssistant would try and perform actions on main controller.
	//All VueJS code should use TTViewStack for accessing views.
	getCurrentOpenPrimaryController() {
		return this.tt_cache.get( 'current_open_primary_controller', 'global' );
	}

	setLastPunchTime( time ) {
		this.tt_cache.set( 'last_punch_time', time, 'user' );
	}

	getLastPunchTime() {
		return this.tt_cache.get( 'last_punch_time', 'user' );
	}

	setPagingType( type ) {
		this.tt_cache.set( 'paging_type', type, 'global' );
	}

	getPagingType() {
		return this.tt_cache.get( 'paging_type', 'global' ) ?? 10;
	}

	setTTAwesomeBoxLayout( layout_name, layout_data ) {
		let layout = this.tt_cache.get( 'tt_awesome_box_layout', 'global' ) || {};
		layout[layout_name] = layout_data;
		this.tt_cache.set( 'tt_awesome_box_layout', layout, 'global' );
	}

	getTTAwesomeBoxLayout( layout_name ) {
		let layout = this.tt_cache.get( 'tt_awesome_box_layout', 'global' ) || {};
		return layout[layout_name] || null;
	}

	setTTAwesomeBoxMetadata( cache_key, metadata ) {
		let cache = this.tt_cache.get( 'tt_awesome_box_metadata', 'global' ) || {};
		cache[cache_key] = metadata;
		this.tt_cache.set( 'tt_awesome_box_metadata', cache, 'global' );
	}

	getTTAwesomeBoxMetadata( cache_key ) {
		let cache = this.tt_cache.get( 'tt_awesome_box_metadata', 'global' ) || {};
		return cache[cache_key] || null;
	}

	setActiveDebugBreakpoints( breakpoints ) {
		this.tt_cache.set( 'active_debug_breakpoints', breakpoints, 'global' );
	}

	getActiveDebugBreakpoints() {
		return this.tt_cache.get( 'active_debug_breakpoints', 'global' ) || [];
	}
}
